/* eslint-disable @typescript-eslint/no-explicit-any */
// ==============================
// Imports
// ==============================

// MUI Components
import AppBarSpacer from '../../components/common/AppBarSpacer';
import {
  Typography,
  Box,
  Button,
  InputAdornment,
  TextField,
  List,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import PageHeader from '../../components/common/PageHeader';

// Libraries
import { useCallback, useEffect, useState } from 'react';
import _ from 'lodash';

// Local components
import MiniDrawer from '../../components/MiniDrawer';
import { ServiceType, Response } from '../../types/ServiceAccordion.type';
import { apiDelete, apiGet } from '../../utils/apiClient';
import { PlusCircle, Search } from 'lucide-react';
import ServiceTypeCard from '../../components/Service/ServiceTypeCard';
import ServiceTypeDialog from '../../components/FormDialog/ServiceTypeDialog';
import { notifyAPIClient, notifyFailure } from '../../utils/toast';
import { SuccessResponse } from '../../types/CourseStageAccordion.type';
import { useTranslation } from '../../i18n';

const HIDDEN_TICKET_SUBTYPE = 'Private Lesson (new name)';

const hidePrivateLessonFromTicket = (serviceTypes: ServiceType[]) =>
  serviceTypes.reduce<ServiceType[]>((visibleServices, service) => {
    if (service.name !== 'TICKET') {
      visibleServices.push(service);
      return visibleServices;
    }

    const visibleSubtypes = service.subtypes.filter(
      (subtype) => subtype.name !== HIDDEN_TICKET_SUBTYPE,
    );

    if (visibleSubtypes.length > 0) {
      visibleServices.push({ ...service, subtypes: visibleSubtypes });
    }

    return visibleServices;
  }, []);

const ServiceSetting = () => {
  const { t } = useTranslation();
  // States
  const [open, setOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [services, setServices] = useState<ServiceType[]>([]);
  const [openServiceDialog, setOpenServiceDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedService, setSelectedService] = useState<ServiceType | null>(
    null,
  );

  const fetchServiceTypes = async () => {
    // Fetch service types from the API
    const res = await apiGet<Response<ServiceType>>(`/service/list`);

    setServices(hidePrivateLessonFromTicket(res.data));
  };

  const filterServiceTypes = useCallback(
    _.debounce(async (value: string) => {
      if (!value) fetchServiceTypes();

      // Fetch service types from the API
      const res = await apiGet<Response<ServiceType>>(
        `/service/filter?name=${value}`,
      );

      setServices(hidePrivateLessonFromTicket(res.data));
    }, 1000),
    [],
  );

  // Handle Add / Edit Service Type
  const handleCloseServiceDialog = () => {
    setOpenServiceDialog(false);
    setSelectedService(null);
  };

  const handleAddServiceType = () => {
    setSelectedService(null);
    setOpenServiceDialog(true);
  };

  const handleEditServiceType = (service: ServiceType) => {
    setSelectedService(service);
    setOpenServiceDialog(true);
  };

  // Handle Delete Service Type
  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
  };

  const handleDeleteServiceType = async (service: ServiceType) => {
    setSelectedService(service);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDeleteServiceType = async (force: boolean) => {
    try {
      const res = await apiDelete<SuccessResponse>(
        `/service/delete/${selectedService?.id}?force=${force}`,
      );

      notifyAPIClient(res.message);
      handleDeleteDialogClose();
      setSelectedService(null);
      fetchServiceTypes();
    } catch (error) {
      console.error('Error deleting service type:', error);
      notifyFailure();
    }
  };

  const handleSearchChange = (e: any) => {
    setSearchTerm(e.target.value);
    filterServiceTypes(e.target.value);
  };

  useEffect(() => {
    fetchServiceTypes();
  }, []);

  // ==============================
  // Render
  // ==============================
  return (
    <>
      <div className="flex h-screen">
        {/* Sidebar */}
        <MiniDrawer open={open} setOpen={setOpen} />

        {/* Main content */}
        <main className="flex-1 overflow-auto app-page-bg">
          {/* Offset for fixed AppBar (height = 64px) */}
          <AppBarSpacer />

          <div className="h-full px-6 py-4">
            <PageHeader
              title={t('serviceSettings.title')}
              subtitle={t('serviceSettings.subtitle')}
            />

            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PlusCircle size={18} />}
                  sx={{
                    color: 'white',
                  }}
                  onClick={handleAddServiceType}
                >
                  {t('serviceSettings.addService')}
                </Button>
              </Box>

              <TextField
                fullWidth
                placeholder={t('serviceSettings.searchPlaceholder')}
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                sx={{ mb: 3, backgroundColor: 'background.paper' }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={20} />
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <List sx={{ width: '100%', pt: 0 }}>
                {services.length === 0 ? (
                  <Typography
                    color="text.secondary"
                    variant="body1"
                    sx={{ textAlign: 'center', py: 4 }}
                  >
                    {searchTerm
                      ? t('serviceSettings.noServicesMatch')
                      : t('serviceSettings.noServices')}
                  </Typography>
                ) : (
                  services.map((service) => (
                    <ServiceTypeCard
                      key={service.id}
                      service={service}
                      onEdit={handleEditServiceType}
                      onDelete={handleDeleteServiceType}
                      fetchServiceTypes={fetchServiceTypes}
                    />
                  ))
                )}
              </List>
            </Box>

            <ServiceTypeDialog
              open={openServiceDialog}
              handleClose={handleCloseServiceDialog}
              serviceType={selectedService}
              fetchServiceTypes={fetchServiceTypes}
            />

            {/* Delete dialog */}
            <Dialog open={openDeleteDialog} onClose={handleDeleteDialogClose}>
              <DialogTitle>{t('serviceSettings.deleteServiceTitle')}</DialogTitle>
              <DialogContent>
                {t('serviceSettings.deleteServiceConfirm')}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleDeleteDialogClose}
                  variant="contained"
                  color="error"
                >
                  {t('common.close')}
                </Button>
                <Button
                  onClick={() => handleConfirmDeleteServiceType(false)}
                  variant="contained"
                  color="primary"
                  className="!text-white"
                >
                  {t('serviceSettings.confirm')}
                </Button>
              </DialogActions>
            </Dialog>
          </div>
        </main>
      </div>
    </>
  );
};

export default ServiceSetting;
